// Service API pour AbracadaTchat - Système de messagerie
import { API_URL } from '../config/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('ecosystem_token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

// ============ CONVERSATIONS ============

export const conversationsAPI = {
  // Récupérer la liste des conversations
  getAll: async (includeArchived = false, limit = 50, offset = 0) => {
    const params = new URLSearchParams({
      include_archived: includeArchived,
      limit: limit.toString(),
      offset: offset.toString()
    });
    const response = await fetch(`${API_URL}/api/messages/conversations?${params}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Erreur lors du chargement des conversations');
    return response.json();
  },

  // Récupérer une conversation spécifique
  getById: async (conversationId) => {
    const response = await fetch(`${API_URL}/api/messages/conversations/${conversationId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Conversation non trouvée');
    return response.json();
  },

  // Créer une nouvelle conversation
  create: async (data) => {
    const response = await fetch(`${API_URL}/api/messages/conversations`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Erreur lors de la création de la conversation');
    }
    return response.json();
  },

  // Archiver/désarchiver une conversation
  toggleArchive: async (conversationId) => {
    const response = await fetch(`${API_URL}/api/messages/conversations/${conversationId}/archive`, {
      method: 'PATCH',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Erreur lors de l\'archivage');
    return response.json();
  }
};

// ============ MESSAGES ============

export const messagesAPI = {
  // Récupérer les messages d'une conversation
  getAll: async (conversationId, limit = 50, beforeId = null) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (beforeId) params.append('before_id', beforeId);
    
    const response = await fetch(`${API_URL}/api/messages/conversations/${conversationId}/messages?${params}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Erreur lors du chargement des messages');
    return response.json();
  },

  // Envoyer un message
  send: async (conversationId, data) => {
    const response = await fetch(`${API_URL}/api/messages/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Erreur lors de l\'envoi du message');
    }
    return response.json();
  },

  // Supprimer un message
  delete: async (messageId) => {
    const response = await fetch(`${API_URL}/api/messages/messages/${messageId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Erreur lors de la suppression');
    return response.json();
  }
};

// ============ PERMISSIONS ============

export const permissionsAPI = {
  // Vérifier si on peut envoyer un message à un utilisateur
  canMessage: async (targetUserId) => {
    const response = await fetch(`${API_URL}/api/messages/can-message/${targetUserId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Erreur lors de la vérification');
    return response.json();
  }
};

// ============ UPLOAD ============

export const uploadAPI = {
  // Uploader un fichier
  uploadFile: async (file) => {
    const token = localStorage.getItem('ecosystem_token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/api/messages/upload`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: formData
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Erreur lors de l\'upload');
    }
    return response.json();
  }
};

// ============ UNREAD COUNT ============

export const unreadAPI = {
  // Récupérer le nombre total de messages non lus
  getCount: async () => {
    const response = await fetch(`${API_URL}/api/messages/unread-count`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) return { unread_count: 0 };
    return response.json();
  }
};

export default {
  conversations: conversationsAPI,
  messages: messagesAPI,
  permissions: permissionsAPI,
  upload: uploadAPI,
  unread: unreadAPI
};
